import { Link } from '@tanstack/react-router'
import type { InvestorData, StartupData, UserData } from '@workspace/api-backend'
import { Avatar, AvatarFallback, AvatarImage } from '@workspace/ui/components/avatar'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import { LabelValue } from '@workspace/ui/components/label-value'
import { ProfileHeader } from '@workspace/ui/components/profile-header'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { getInitials } from '@workspace/ui/lib/utils'
import { Building2, Check, Eye, FileText, Globe, LinkIcon, MapPin, Pencil } from 'lucide-react'
import { useState } from 'react'
import { DocumentPreviewModal, type PreviewDocument } from '@/features/preview'

type ProfileViewProps = {
  user: UserData
  canEdit: boolean
  onEdit?: () => void
  startup?: StartupData | null
  investor?: InvestorData | null
}

export function ProfileView(props: ProfileViewProps) {
  const { user, canEdit, onEdit, startup, investor } = props
  const isFounder = user.userType === 'FOUNDER'
  const isInvestor = user.userType === 'INVESTOR'
  const resumeDocument = user.resumeDocument ?? null

  // Compute subtitle based on role
  const getSubtitle = () => {
    if (isInvestor && investor) {
      return investor.name ?? user?.location
    }
    return user?.location
  }

  const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null)
  const [copied, setCopied] = useState(false)

  const handleCopyLink = () => {
    const url = `${window.location.origin}/users/${user.id}`
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <>
      <ProfileHeader
        avatarUrl={user.image}
        coverImageUrl={user.coverImage}
        name={user.name ?? user.email}
        subtitle={getSubtitle()}
        badges={
          <Badge variant="outline-primary" size="md">
            {isFounder ? 'Founder' : 'Investor'}
          </Badge>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleCopyLink}>
              {copied ? <Check className="size-4" /> : <LinkIcon className="size-4" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            {canEdit && onEdit && (
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Pencil className="size-4" />
                Edit Profile
              </Button>
            )}
          </div>
        }
        contentClassName="mx-auto max-w-6xl"
      />

      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center gap-6">
          <LabelValue
            icon={<MapPin className="size-4" />}
            label="Location"
            value={user.location}
            direction="horizontal"
          />
          {user.websiteUrl && (
            <a
              href={user.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[#f97316] hover:underline"
            >
              <Globe className="size-4" />
              <span className="text-sm">{user.websiteUrl.replace(/^https?:\/\//, '')}</span>
            </a>
          )}
        </div>

        {user.bio && (
          <ProfileSection>
            <p className="text-white leading-relaxed">{user.bio}</p>
          </ProfileSection>
        )}

        {isFounder && resumeDocument && (
          <ProfileSection title="Resume">
            <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
              <div className="flex items-center gap-3">
                <FileText className="size-8 text-muted-foreground" />
                <div>
                  <p className="font-medium text-sm">{resumeDocument.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {resumeDocument.fileSize
                      ? `${Math.round(resumeDocument.fileSize / 1024)} KB`
                      : 'PDF'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setPreviewDocument({
                    id: resumeDocument.id,
                    fileName: resumeDocument.fileName,
                    mimeType: resumeDocument.mimeType,
                  })
                }
              >
                <Eye className="size-4" />
                Preview
              </Button>
            </div>
          </ProfileSection>
        )}

        {(user.linkedinUrl || user.twitterUrl || user.githubUrl) && (
          <ProfileSection title="Social">
            <div className="flex flex-wrap gap-3">
              {user.linkedinUrl && (
                <a
                  href={user.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-input/30 hover:bg-input/50 transition-colors text-white text-sm"
                >
                  <svg
                    className="size-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn
                </a>
              )}
              {user.twitterUrl && (
                <a
                  href={user.twitterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-input/30 hover:bg-input/50 transition-colors text-white text-sm"
                >
                  <svg
                    className="size-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  Twitter
                </a>
              )}
              {user.githubUrl && (
                <a
                  href={user.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-input/30 hover:bg-input/50 transition-colors text-white text-sm"
                >
                  <svg
                    className="size-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                  </svg>
                  GitHub
                </a>
              )}
            </div>
          </ProfileSection>
        )}

        {isFounder && startup && (
          <ProfileSection title={canEdit ? 'My Startup' : 'Startup'}>
            {canEdit ? (
              <Link to="/" className="block">
                <div className="flex items-center gap-4 p-4 rounded-xl border border-border bg-input/30 hover:bg-input/50 transition-colors cursor-pointer">
                  <Avatar className="size-12 ring-2 ring-border shrink-0">
                    {startup.logoUrl && <AvatarImage src={startup.logoUrl} alt={startup.name} />}
                    <AvatarFallback className="bg-input text-sm font-medium text-white">
                      {getInitials(startup.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white">{startup.name}</p>
                    <p className="text-sm text-muted-foreground">{startup.category?.name}</p>
                  </div>
                  <Building2 className="size-5 text-muted-foreground" />
                </div>
              </Link>
            ) : (
              <div className="flex items-center gap-4 p-4 rounded-xl border border-border bg-input/30">
                <Avatar className="size-12 ring-2 ring-border shrink-0">
                  {startup.logoUrl && <AvatarImage src={startup.logoUrl} alt={startup.name} />}
                  <AvatarFallback className="bg-input text-sm font-medium text-white">
                    {getInitials(startup.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white">{startup.name}</p>
                  <p className="text-sm text-muted-foreground">{startup.category?.name}</p>
                </div>
                <Building2 className="size-5 text-muted-foreground" />
              </div>
            )}
          </ProfileSection>
        )}

        {isInvestor && investor && (
          <ProfileSection title={canEdit ? 'My Investor Entity' : 'Investor Entity'}>
            <Link
              to={canEdit ? '/' : '/investors/$id'}
              {...(!canEdit && { params: { id: investor.id } })}
              className="block"
            >
              <div className="flex items-center gap-4 p-4 rounded-xl border border-border bg-input/30 hover:bg-input/50 transition-colors cursor-pointer">
                <div className="flex items-center justify-center size-12 rounded-full bg-input ring-2 ring-border shrink-0">
                  <Building2 className="size-6 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white">{investor.name}</p>
                </div>
                <Building2 className="size-5 text-muted-foreground" />
              </div>
            </Link>
          </ProfileSection>
        )}
      </div>

      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open
          onOpenChange={(open) => !open && setPreviewDocument(null)}
        />
      )}
    </>
  )
}
